<template>  
  <main class="min-h-screen bg-white overflow-hidden">
    <!-- Background elements -->
    <UnderwaterBg class="fixed inset-0 z-0" />
    
    <!-- Header/Navigation -->
    <the-header
      :is-user-authenticated="isUserAuthenticated"
      @open-login="showLoginDialog = true"
    />    <!-- Main Content with enhanced styling -->
    <div class="relative z-10 px-6 md:px-10 max-w-7xl mx-auto">
      <!-- Hero Section -->
      <hero-section @scroll-to="scrollToSection" class="mb-8 md:mb-12" />
      
      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>

      <!-- How It Works + Rarity System Section -->
      <how-it-works-section @scroll-to-section="scrollToSection" class="mb-8 md:mb-12" />
      
      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>

      <!-- Pricing Section -->
      <div class="relative mb-8 md:mb-12" id="pricing">
        <pricing-section />
      </div>

      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>

      <!-- About Us Section -->
      <about-us-section class="mb-8 md:mb-12" />


      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>

      <!-- Leaderboard Details Section -->
      <leaderboard-section 
        :reduced-motion="reducedMotion"
        @scroll-to-section="scrollToSection" 
        class="mb-8 md:mb-12" 
      />

      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>

      <!-- Demo Section -->
      <demo-section class="mb-8 md:mb-12" />
      
      <!-- Duck Section Divider -->
      <div class="duck-section-divider flex items-center justify-center my-6 md:my-10">
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
        <div class="mx-4">
          <v-icon color="yellow" size="small" :class="{'animate-float-subtle': !reducedMotion}">mdi-duck</v-icon>
        </div>
        <div class="h-0.5 bg-gray-200 w-full max-w-xs"></div>
      </div>
      
      <!-- CTA Section - Final push to action -->
      <div class="relative mb-8">
        <div class="absolute -inset-1 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-blue-500/10 to-yellow-500/20 blur-lg opacity-50"></div>
        <div class="relative">
          <CTASection />
        </div>
      </div>
    </div>

    <!-- Login Dialog Component -->
    <LoginDialog
      v-model="showLoginDialog"
      :redirect-url="redirectUrl"
      :show-message="loginMessage"
      @login-success="onLoginSuccess"
      @notification="showNotificationMessage"
    />
    
    <!-- Notifications -->
    <v-snackbar
      v-model="showNotification"
      :color="notificationColor"
      :timeout="3000"
      location="top"
    >
      {{ notificationMessage }}
    </v-snackbar>

    <!-- Scroll to Top Button -->
    <transition name="fade">
      <button 
        v-show="showScrollTop" 
        @click="scrollToTop" 
        class="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-yellow-500 text-white flex items-center justify-center shadow-lg hover:bg-yellow-600 transition-all"
        aria-label="Scroll to top"
      >
        <v-icon>mdi-chevron-up</v-icon>
      </button>
    </transition>

    <!-- Footer -->
    <the-footer />
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, onBeforeUnmount, nextTick } from 'vue';
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { useAuthStore } from '@/stores/authStore';
import { useRoute, useRouter } from 'vue-router';
import LoginDialog from '@/components/auth/LoginDialog.vue';
import { usePackagesStore } from '@/stores/packagesStore';

// Import components
import TheHeader from '@/components/layout/TheHeader.vue';
import TheFooter from '@/components/layout/TheFooter.vue';
import HeroSection from '@/components/home/HeroSection.vue';
import HowItWorksSection from '@/components/home/HowItWorksSection.vue';
import PricingSection from '@/components/home/PricingSection.vue';
import CTASection from '@/components/home/CTASection.vue';
import DemoSection from '@/components/home/DemoSection.vue';
import AboutUsSection from '@/components/home/AboutUsSection.vue';
import LeaderboardSection from '@/components/home/LeaderboardSection.vue';

// Router, route, and auth store
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// Computed property to check if user is authenticated
const isUserAuthenticated = computed(() => {
  return authStore.isAuthenticated();
});

// Add back the login dialog state
const showLoginDialog = ref(false);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationColor = ref('success');

// Get redirect URL from query parameter if present
const redirectUrl = ref(route.query.redirect as string || '');
const loginMessage = ref(route.query.message as string || '');

// Add packages store
const packagesStore = usePackagesStore();

// Scroll to top button visibility
const showScrollTop = ref(false);

// Check if user prefers reduced motion
const reducedMotion = ref(false);

// Handle scroll events for showing/hiding scroll to top button
const handleScroll = () => {
  showScrollTop.value = window.scrollY > 500;
};

// Scroll to top function
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

onMounted(() => {
  // Check for reduced motion preference
  reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Add scroll event listener
  window.addEventListener('scroll', handleScroll);
  
  // Initialize authentication state first
  authStore.initAuth();
  
  // Pre-fetch packages data early for faster pricing section loading
  packagesStore.fetchPackages();

  // Check for hash in URL and scroll to that section if present
  if (window.location.hash) {
    const id = window.location.hash.substring(1); // Remove the # character
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500); // Small delay to ensure DOM is ready
  }
  
  // Use nextTick to ensure authentication state is properly initialized
  // before checking showLogin parameter
  nextTick(() => {
    checkAndOpenLoginDialog();
  });
});

// Function to check showLogin parameter and open dialog
const checkAndOpenLoginDialog = () => {
  if (route.query.showLogin === 'true' && !isUserAuthenticated.value) {
    showLoginDialog.value = true;
    
    // Show login message if provided
    if (loginMessage.value) {
      showNotification.value = true;
      notificationMessage.value = loginMessage.value;
      notificationColor.value = 'info';
    }
    
    // Clear the showLogin query parameter from URL after opening dialog
    const newQuery = { ...route.query };
    delete newQuery.showLogin;
    router.replace({ path: route.path, query: newQuery });
  }
};

// Clean up event listeners
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});

// Scroll to section
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: reducedMotion.value ? 'auto' : 'smooth' });
  }
};

// Watch for route changes to update redirect URL
watch(() => route.query.redirect, (newRedirect) => {
  if (newRedirect) {
    redirectUrl.value = newRedirect as string;
  }
});

// Watch for showLogin query parameter changes to automatically open login dialog
watch(() => route.query.showLogin, (newShowLogin) => {
  if (newShowLogin === 'true' && !isUserAuthenticated.value) {
    nextTick(() => {
      checkAndOpenLoginDialog();
    });
  }
});

// Handle notification from LoginDialog component
const showNotificationMessage = ({ message, color }: { message: string, color?: string }) => {
  notificationMessage.value = message;
  notificationColor.value = color || 'info';
  showNotification.value = true;
};

// Handle successful login - redirect to admin view
const onLoginSuccess = () => {
  router.push('/admin');
};
</script>

<style>
/* Global styles */
.text-gray-700 {
  color: rgba(0, 0, 0, 0.8);
}

/* Message bubble styles */
.message-bubble {
  background-color: rgba(234, 179, 8, 0.1);
}

.message-bubble:before {
  border-color: transparent transparent rgba(234, 179, 8, 0.1) transparent;
}
</style>

<style scoped>
/* Duck Section Divider */
.duck-section-divider {
  position: relative;
  overflow: visible;
}

.duck-section-divider::before,
.duck-section-divider::after {
  content: '';
  position: absolute;
  height: 0.5px;
  background-color: rgba(234, 179, 8, 0.1);
  width: 50px;
  top: 50%;
}

.duck-section-divider::before {
  right: calc(50% + 2rem);
}

.duck-section-divider::after {
  left: calc(50% + 2rem);
}

/* Remove old section dividers */
.section-divider,
.wave-divider,
.curved-divider {
  display: none;
}

/* Animation for scroll to top button */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animations for floating elements */
.animate-float {
  animation: float 3s ease-in-out infinite;
  will-change: transform;
}

.animate-float-slow {
  animation: float 8s ease-in-out infinite;
  will-change: transform;
}

.animate-float-delay {
  animation: float 6s ease-in-out infinite;
  animation-delay: 2s;
  will-change: transform;
}

.animate-float-reverse {
  animation: floatReverse 7s ease-in-out infinite;
  animation-delay: 1s;
  will-change: transform;
}

.animate-float-subtle {
  animation: floatSubtle 5s ease-in-out infinite;
  will-change: transform;
}

/* Animation Keyframes */
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

@keyframes floatReverse {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(-3deg); }
}

@keyframes floatSubtle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .animate-float,
  .animate-float-slow,
  .animate-float-delay,
  .animate-float-reverse {
    animation-duration: 4s;
    max-width: 200px;
    max-height: 200px;
  }
}

/* Focus states for accessibility */
a:focus, button:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .animate-float,
  .animate-float-slow,
  .animate-float-delay,
  .animate-float-reverse,
  .animate-float-subtle {
    animation: none;
  }
  
  * {
    transition-duration: 0.1s !important;
  }
}

/* Yellow color classes */
.text-yellow-400 {
  color: #facc15;
}

.text-yellow-500 {
  color: #eab308;
}

.bg-yellow-500 {
  background-color: #eab308;
}

.bg-yellow-600 {
  background-color: #ca8a04;
}

.bg-yellow-500\/20 {
  background-color: rgba(234, 179, 8, 0.2);
}

.from-yellow-500\/20 {
  --tw-gradient-from: rgba(234, 179, 8, 0.2);
}
</style>